// Dedicated component for the inline text-edit contentEditable.
//
// ── Architecture ────────────────────────────────────────────────────────────
// Active-edit state is owned by the PARENT in a ref (`liveTextRef`), not by
// this component or by parent props. Reason: even if React or some other
// machinery remounts this component mid-edit, it can re-hydrate from the
// LIVE typed text (in the ref) instead of from a stale `initialText` prop
// (which carries `element.text` from before the user started typing). Means
// "stale element.text overwrites live DOM" is impossible by construction.
//
// On every keystroke, onInput pushes the current serialized text into
// `liveTextRef.current`. On mount, useLayoutEffect prefers `liveTextRef`'s
// value over `initialText` if it has one. On unmount, the ref is cleared by
// the parent (via onBlur consume path).
//
// ── Debug instrumentation ──────────────────────────────────────────────────
// When `debug` is true, this component logs:
//   - mount #N    (counter — if you see >1 per edit session, something is
//                  remounting us mid-edit; that's a smoking gun for the
//                  "text reverts" bug since each remount runs hydration)
//   - hydrate    (textContent assignment, with source: ref or prop)
//   - input      (each keystroke, with sample of current textContent)
//   - blur       (final serialized text)
//
// Mount counter is a window-global so it survives the component instance.

import React, {
  useRef,
  useImperativeHandle,
  useLayoutEffect,
  forwardRef,
} from 'react';
import {
  serializeContentEditable,
  insertNewlineAtCaret,
  insertPlainTextAtCaret,
} from '../../lib/text/contentEditableSerializer';

const bumpMountCounter = (id) => {
  if (typeof window === 'undefined') return 1;
  window.__editableMountCounts__ = window.__editableMountCounts__ || {};
  window.__editableMountCounts__[id] = (window.__editableMountCounts__[id] || 0) + 1;
  return window.__editableMountCounts__[id];
};

const EditableTextNode = forwardRef(function EditableTextNode(props, fwdRef) {
  const {
    initialText,
    initialCaret,
    style,
    liveTextRef,            // parent-owned ref holding the live typed text
    onInput,                // parent debounce / auto-grow hook
    onBlur,                 // parent commit hook (receives serialized text)
    debug,
    debugId,
  } = props;

  const nodeRef = useRef(null);

  useImperativeHandle(fwdRef, () => ({
    getText: () => serializeContentEditable(nodeRef.current),
    getDebugSnapshot: () => {
      const n = nodeRef.current;
      if (!n) return null;
      return {
        innerHTML: n.innerHTML,
        textContent: n.textContent,
        serialized: serializeContentEditable(n),
        scrollHeight: n.scrollHeight,
        childNodes: Array.from(n.childNodes).map((c) =>
          c.nodeType === 3
            ? { kind: 'text', value: c.nodeValue }
            : { kind: c.tagName, html: c.outerHTML }
        ),
      };
    },
    blur: () => nodeRef.current?.blur(),
    focus: () => nodeRef.current?.focus(),
  }), []);

  // Single-shot hydration on mount. Empty deps — never re-runs for this
  // component instance. If REMOUNT happens (e.g. parent decided to remount
  // us), the ref-based liveTextRef recovers the user's latest typed text,
  // so even a remount cannot overwrite live typing with stale prop data.
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    const mountN = bumpMountCounter(debugId);
    const liveText = liveTextRef?.current;
    const hasLive = typeof liveText === 'string' && liveText.length > 0;
    const seed = hasLive ? liveText : (initialText ?? '');
    node.textContent = seed;

    if (debug) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(`[EditableTextNode:mount #${mountN}] ${debugId || ''}`);
      console.log('source     :', hasLive ? 'liveTextRef (recovered)' : 'initialText (fresh)');
      console.log('seed value :', JSON.stringify(seed));
      console.log('initialText prop:', JSON.stringify(initialText));
      console.log('liveTextRef.current:', JSON.stringify(liveText));
      console.log('mount count for this id:', mountN);
      if (mountN > 1) {
        console.warn('⚠ This element has remounted while editing.');
      }
      console.groupEnd();
    }

    // Seed liveTextRef with the initial value so subsequent mounts (if any)
    // see the most-recent text. The parent clears this on blur to avoid
    // leaking into the next edit session.
    if (liveTextRef && !hasLive) {
      liveTextRef.current = seed;
    }

    node.focus();
    const sel = window.getSelection?.();
    if (!sel) return;

    const range = document.createRange();
    let placed = false;
    if (initialCaret && !hasLive /* fresh edit — honor click point */) {
      try {
        const cp =
          (document.caretPositionFromPoint &&
            document.caretPositionFromPoint(initialCaret.clientX, initialCaret.clientY)) ||
          (document.caretRangeFromPoint &&
            document.caretRangeFromPoint(initialCaret.clientX, initialCaret.clientY));
        if (cp) {
          if ('offsetNode' in cp) {
            range.setStart(cp.offsetNode, cp.offset);
            range.collapse(true);
          } else {
            range.setStart(cp.startContainer, cp.startOffset);
            range.collapse(true);
          }
          if (node.contains(range.startContainer)) placed = true;
        }
      } catch { /* fall through */ }
    }
    if (!placed) {
      range.selectNodeContents(node);
      range.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(range);

    if (debug) {
      // eslint-disable-next-line no-console
      console.log(`[EditableTextNode:hydrated] ${debugId || ''} DOM ready`,
        JSON.stringify(node.textContent));
    }

    return () => {
      if (debug) {
        // eslint-disable-next-line no-console
        console.log(`[EditableTextNode:unmount] ${debugId || ''} (was mount #${mountN})`);
      }
    };
    // Mount-only by contract; props read here are SNAPSHOTS.
  }, []);

  return (
    <div
      ref={nodeRef}
      contentEditable="plaintext-only"
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      style={style}
      onInput={(e) => {
        // Persist live typing to the parent ref BEFORE anything else, so a
        // remount on the same tick recovers what the user just typed.
        if (liveTextRef) {
          liveTextRef.current = serializeContentEditable(e.currentTarget);
        }
        if (debug) {
          // eslint-disable-next-line no-console
          console.log(`[EditableTextNode:input] ${debugId || ''}`,
            JSON.stringify(e.currentTarget.textContent),
            `sh=${e.currentTarget.scrollHeight}`,
            `liveRef=${JSON.stringify(liveTextRef?.current)}`);
        }
        onInput?.(e);
      }}
      onBlur={(e) => {
        const serialized = serializeContentEditable(e.currentTarget);
        if (debug) {
          // eslint-disable-next-line no-console
          console.groupCollapsed(`[EditableTextNode:blur] ${debugId || ''}`);
          console.log('innerHTML  :', e.currentTarget.innerHTML);
          console.log('textContent:', JSON.stringify(e.currentTarget.textContent));
          console.log('serialized :', JSON.stringify(serialized));
          console.log('liveRef    :', JSON.stringify(liveTextRef?.current));
          console.groupEnd();
        }
        onBlur?.(e, serialized);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.currentTarget.blur();
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
          e.preventDefault();
          e.stopPropagation();
          insertNewlineAtCaret(e.currentTarget);
          return;
        }
        e.stopPropagation();
      }}
      onPaste={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const text = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
        if (text) insertPlainTextAtCaret(e.currentTarget, text);
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    />
  );
});

export default EditableTextNode;
